import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
    Box,
    Button,
    IconButton,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    CircularProgress,
    Slider,
    Checkbox,
    FormControlLabel,
    Select,
    MenuItem
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import {
    ArrowForward,
    Collections as CollectionsIcon,
    CloudUpload as UploadIcon,
    CompareArrows as CompareIcon,
    Delete as DeleteIcon,
    Close as CloseIcon,
    ChevronLeft,
    ChevronRight,
    PhotoCamera as CameraIcon,
    Edit as EditIcon,
    Check as CheckIcon,
    ZoomIn as ZoomInIcon,
    ZoomOut as ZoomOutIcon,
    RestartAlt as ResetIcon,
    Download as DownloadIcon,
    Share as ShareIcon,
    CloudUploadOutlined as UploadOutlinedIcon,
    MonitorWeightOutlined as WeightIcon
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { useMessageContext } from "../contexts/MessageContext";
import { RTL } from "../cache/CacheRtl";
import Loader from "../componenets/Loader";
import UploadProgressIndicator from "../componenets/common/UploadProgressIndicator";
import { uploadImage } from "../services/MediaUploadService";
import { imageUrl } from "../utils/imageUrl";
import { formatPhotoWeight } from "../utils/progressPhotoWeight";
import {
    getProgressPhotos,
    addProgressPhoto,
    deleteProgressPhoto,
    deleteProgressPhotosByDate,
    saveDateComment
} from "../services/ProgressPhotosService";

const PAGE_SIZE = 20;

// Robust, cross-browser date parsing.
// `new Date(string)` is unreliable in Safari/Firefox: it rejects non-strict-ISO
// formats (e.g. "2026-05-19 00:00:00") and date-array shapes that Chrome's lenient
// parser happens to accept. The backend serializes dates as a LocalDate array
// ([year, month(1-based), day]), which becomes the string "2026,5,19" when used as
// an object key. Handle arrays, comma-separated values, and ISO strings explicitly,
// then fall back to dayjs.
const parsePhotoDate = (value) => {
    if (value === null || value === undefined || value === '') return null;

    // [year, month(1-12), day, ...] arrays, or comma-joined strings like "2026,5,19"
    const parts = Array.isArray(value)
        ? value
        : (typeof value === 'string' && value.includes(',') ? value.split(',') : null);
    if (parts && parts.length >= 3) {
        const [year, month, day] = parts.map(Number);
        if (![year, month, day].some(Number.isNaN)) {
            const date = new Date(year, month - 1, day);
            return Number.isNaN(date.getTime()) ? null : date;
        }
    }

    const str = String(value);
    const match = str.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (match) {
        const [, year, month, day] = match;
        const date = new Date(Number(year), Number(month) - 1, Number(day));
        return Number.isNaN(date.getTime()) ? null : date;
    }

    const parsed = dayjs(str);
    return parsed.isValid() ? parsed.toDate() : null;
};

// Canonical YYYY-MM-DD key. Used for grouping and for any value sent back to the
// API (delete-by-date, save-comment), which expect a YYYY-MM-DD string — not the
// raw "2026,5,19" array-stringification.
const toISODateKey = (value) => {
    const date = parsePhotoDate(value);
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const formatDate = (dateString) => {
    const date = parsePhotoDate(dateString);
    if (!date) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
};

const getDayName = (dateString) => {
    const date = parsePhotoDate(dateString);
    if (!date) return '';
    const days = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];
    return days[date.getDay()];
};

const loadImageCORS = (url) => new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load: ${url}`));
    // Add cache-buster to avoid browser serving a cached non-CORS response
    // (the browser caches images loaded without crossOrigin separately)
    const separator = url.includes('?') ? '&' : '?';
    img.src = `${url}${separator}cors=1`;
});

const loadImageFromUrl = (url) => new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load: ${url}`));
    img.src = url;
});

const roundRect = (ctx, x, y, w, h, r) => {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
};

const generateComparisonImage = async (photo1, photo2, settings = {}) => {
    const {
        logoDataUrl = null,
        logoPosition = 'bottom-left',
        logoSize = 16,
        showDates = true,
        quality = 'high'
    } = settings;

    const PADDING = 40;
    const GAP = 24;
    const TARGET_H = quality === 'high' ? 600 : quality === 'medium' ? 450 : 300;
    const weight1 = formatPhotoWeight(photo1);
    const weight2 = formatPhotoWeight(photo2);
    const TEXT_H = showDates ? (weight1 || weight2 ? 84 : 60) : 0;
    const LOGO_PAD = 12;

    // Load images with CORS enabled (CloudFront returns Access-Control-Allow-Origin: *)
    const [img1, img2] = await Promise.all([
        loadImageCORS(photo1.photoUrl),
        loadImageCORS(photo2.photoUrl)
    ]);

    // Load logo if provided
    let logoImg = null;
    if (logoDataUrl) {
        logoImg = await loadImageFromUrl(logoDataUrl).catch(() => null);
    }

    // Both photos share an identical cell (same width, height, scale) so the
    // comparison is fair regardless of each photo's native aspect ratio. Each
    // image fills its cell (object-fit: cover), cropping overflow so no
    // letterbox margins appear.
    const CELL_H = TARGET_H;
    const CELL_W = Math.round(TARGET_H * 3 / 4);

    // Canvas dimensions
    const canvasW = PADDING + CELL_W + GAP + CELL_W + PADDING;
    const canvasH = PADDING + CELL_H + TEXT_H + PADDING;

    const canvas = document.createElement('canvas');
    canvas.width = canvasW;
    canvas.height = canvasH;
    const ctx = canvas.getContext('2d');

    // Background
    ctx.fillStyle = '#1E293B';
    ctx.fillRect(0, 0, canvasW, canvasH);

    const x1 = PADDING;
    const x2 = PADDING + CELL_W + GAP;
    const imgY = PADDING;

    const drawCover = (img, cellX) => {
        const scale = Math.max(CELL_W / img.naturalWidth, CELL_H / img.naturalHeight);
        const drawW = Math.round(img.naturalWidth * scale);
        const drawH = Math.round(img.naturalHeight * scale);
        const dx = cellX + Math.round((CELL_W - drawW) / 2);
        const dy = imgY + Math.round((CELL_H - drawH) / 2);

        ctx.save();
        roundRect(ctx, cellX, imgY, CELL_W, CELL_H, 8);
        ctx.clip();
        ctx.drawImage(img, dx, dy, drawW, drawH);
        ctx.restore();
    };

    drawCover(img1, x1);
    drawCover(img2, x2);

    // Arrow between photos
    ctx.font = '700 28px Arial';
    ctx.fillStyle = '#14B8A6';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('→', PADDING + CELL_W + GAP / 2, imgY + CELL_H / 2);

    // Date text
    if (showDates) {
        const textY = imgY + CELL_H + 24;
        const font = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif';

        ctx.textBaseline = 'top';
        ctx.textAlign = 'center';

        ctx.font = `600 20px ${font}`;
        ctx.fillStyle = '#FFFFFF';
        ctx.fillText(formatDate(photo1.photoDate), x1 + CELL_W / 2, textY);
        ctx.fillText(formatDate(photo2.photoDate), x2 + CELL_W / 2, textY);

        ctx.font = `400 14px ${font}`;
        ctx.fillStyle = '#94A3B8';
        ctx.fillText(`יום ${getDayName(photo1.photoDate)}`, x1 + CELL_W / 2, textY + 24);
        ctx.fillText(`יום ${getDayName(photo2.photoDate)}`, x2 + CELL_W / 2, textY + 24);

        ctx.font = `600 15px ${font}`;
        ctx.fillStyle = '#5EEAD4';
        if (weight1) ctx.fillText(weight1, x1 + CELL_W / 2, textY + 46);
        if (weight2) ctx.fillText(weight2, x2 + CELL_W / 2, textY + 46);
    }

    // Logo
    if (logoImg) {
        const logoPx = Math.round((logoSize / 100) * TARGET_H);
        const logoAspect = logoImg.naturalWidth / logoImg.naturalHeight;
        const logoW = logoPx;
        const logoH = Math.round(logoPx / logoAspect);

        const isTop = logoPosition.startsWith('top');
        const isRight = logoPosition.endsWith('right');

        const lx = isRight ? canvasW - PADDING - logoW - LOGO_PAD : PADDING + LOGO_PAD;
        const ly = isTop ? PADDING + LOGO_PAD : imgY + TARGET_H - logoH - LOGO_PAD;

        ctx.globalAlpha = 0.85;
        ctx.drawImage(logoImg, lx, ly, logoW, logoH);
        ctx.globalAlpha = 1.0;
    }

    return canvas.toDataURL('image/png');
};

const downloadDataUrl = (dataUrl, filename) => {
    const link = document.createElement('a');
    link.download = filename;
    link.href = dataUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

// --- Share Preview Dialog ---
const LOGO_POSITIONS = [
    { value: 'top-right', label: 'למעלה ימין' },
    { value: 'top-left', label: 'למעלה שמאל' },
    { value: 'bottom-right', label: 'למטה ימין' },
    { value: 'bottom-left', label: 'למטה שמאל' }
];

const SharePreviewDialog = ({ open, onClose, photo1, photo2 }) => {
    const [logoDataUrl, setLogoDataUrl] = useState(null);
    const [logoPosition, setLogoPosition] = useState('bottom-left');
    const [logoSize, setLogoSize] = useState(16);
    const [showDates, setShowDates] = useState(true);
    const [quality, setQuality] = useState('high');
    const [previewUrl, setPreviewUrl] = useState(null);
    const [generating, setGenerating] = useState(false);
    const fileInputRef = useRef(null);
    const debounceRef = useRef(null);

    const currentSettings = useMemo(() => ({
        logoDataUrl, logoPosition, logoSize, showDates, quality
    }), [logoDataUrl, logoPosition, logoSize, showDates, quality]);

    // Generate preview when settings change
    useEffect(() => {
        if (!open || !photo1 || !photo2) return;

        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
            setGenerating(true);
            try {
                const url = await generateComparisonImage(photo1, photo2, currentSettings);
                setPreviewUrl(url);
            } catch (err) {
                console.error('Preview generation error:', err);
            } finally {
                setGenerating(false);
            }
        }, 300);

        return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    }, [open, photo1, photo2, currentSettings]);

    const handleLogoUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => setLogoDataUrl(ev.target.result);
        reader.readAsDataURL(file);
    };

    const handleRemoveLogo = () => {
        setLogoDataUrl(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleDownload = () => {
        if (!previewUrl) return;
        const date1 = formatDate(photo1.photoDate).replace(/\//g, '-');
        const date2 = formatDate(photo2.photoDate).replace(/\//g, '-');
        downloadDataUrl(previewUrl, `progress-comparison-${date1}_${date2}.png`);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{
            sx: { borderRadius: '16px', overflow: 'hidden' }
        }}>
            <RTL>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, pb: 0 }}>
                <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#1E293B' }}>
                    ייצוא השוואה עם לוגו
                </Typography>
                <IconButton onClick={onClose} sx={{ color: '#64748B' }}>
                    <CloseIcon />
                </IconButton>
            </Box>

            <DialogContent sx={{ display: 'flex', gap: 3, p: 3 }}>
                {/* Settings - Right side (first in RTL) */}
                <Box sx={{ width: '280px', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#1E293B' }}>
                        הגדרות
                    </Typography>

                    {/* Logo Upload */}
                    <Box>
                        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#475569', mb: 1 }}>
                            לוגו:
                        </Typography>
                        {logoDataUrl ? (
                            <Box sx={{
                                border: '2px solid #E2E8F0',
                                borderRadius: '10px',
                                p: 1.5,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                            }}>
                                <Button size="small" onClick={handleRemoveLogo} sx={{ color: '#EF4444', textTransform: 'none', fontSize: '12px' }}>
                                    הסר
                                </Button>
                                <img src={logoDataUrl} alt="Logo" style={{ height: '40px', width: 'auto', borderRadius: '4px' }} />
                            </Box>
                        ) : (
                            <Box
                                onClick={() => fileInputRef.current?.click()}
                                sx={{
                                    border: '2px dashed #CBD5E1',
                                    borderRadius: '10px',
                                    p: 2.5,
                                    textAlign: 'center',
                                    cursor: 'pointer',
                                    '&:hover': { borderColor: '#14B8A6', backgroundColor: '#F0FDFA' },
                                    transition: 'all 0.2s'
                                }}
                            >
                                <UploadOutlinedIcon sx={{ fontSize: '28px', color: '#94A3B8', mb: 0.5 }} />
                                <Typography sx={{ fontSize: '13px', color: '#64748B', fontWeight: 500 }}>
                                    לחץ להעלאת לוגו
                                </Typography>
                                <Typography sx={{ fontSize: '11px', color: '#94A3B8' }}>
                                    PNG, JPG או SVG
                                </Typography>
                            </Box>
                        )}
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/png,image/jpeg,image/svg+xml"
                            style={{ display: 'none' }}
                            onChange={handleLogoUpload}
                        />
                    </Box>

                    {/* Logo Position */}
                    {logoDataUrl && (
                        <Box>
                            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#475569', mb: 1 }}>
                                מיקום לוגו:
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {LOGO_POSITIONS.map(pos => (
                                    <Button
                                        key={pos.value}
                                        size="small"
                                        variant={logoPosition === pos.value ? 'contained' : 'outlined'}
                                        onClick={() => setLogoPosition(pos.value)}
                                        sx={{
                                            textTransform: 'none',
                                            fontSize: '12px',
                                            fontWeight: 500,
                                            borderRadius: '8px',
                                            minWidth: 'auto',
                                            px: 1.5,
                                            ...(logoPosition === pos.value
                                                ? { backgroundColor: '#14B8A6', '&:hover': { backgroundColor: '#0D9488' } }
                                                : { borderColor: '#E2E8F0', color: '#475569', '&:hover': { borderColor: '#14B8A6' } }
                                            )
                                        }}
                                    >
                                        {pos.label}
                                    </Button>
                                ))}
                            </Box>
                        </Box>
                    )}

                    {/* Logo Size */}
                    {logoDataUrl && (
                        <Box>
                            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#475569', mb: 0.5 }}>
                                גודל לוגו:
                            </Typography>
                            <Slider
                                value={logoSize}
                                onChange={(_, val) => setLogoSize(val)}
                                min={5}
                                max={40}
                                sx={{ color: '#14B8A6' }}
                            />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography sx={{ fontSize: '11px', color: '#94A3B8' }}>גדול</Typography>
                                <Typography sx={{ fontSize: '12px', color: '#475569', fontWeight: 600 }}>{logoSize}%</Typography>
                                <Typography sx={{ fontSize: '11px', color: '#94A3B8' }}>קטן</Typography>
                            </Box>
                        </Box>
                    )}

                    {/* Show Dates */}
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={showDates}
                                onChange={(e) => setShowDates(e.target.checked)}
                                sx={{ color: '#14B8A6', '&.Mui-checked': { color: '#14B8A6' } }}
                            />
                        }
                        label={<Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#475569' }}>הצג תאריכים על התמונות</Typography>}
                        sx={{ mx: 0, backgroundColor: '#F8FAFC', borderRadius: '10px', px: 1, py: 0.5 }}
                    />

                    {/* Quality */}
                    <Box>
                        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#475569', mb: 1 }}>
                            איכות ייצוא:
                        </Typography>
                        <Select
                            value={quality}
                            onChange={(e) => setQuality(e.target.value)}
                            size="small"
                            fullWidth
                            sx={{ borderRadius: '8px', fontSize: '13px' }}
                        >
                            <MenuItem value="high">גבוהה (מודפס)</MenuItem>
                            <MenuItem value="medium">בינונית (רשתות חברתיות)</MenuItem>
                            <MenuItem value="low">נמוכה (מהיר)</MenuItem>
                        </Select>
                    </Box>
                </Box>

                {/* Preview - Left side (second in RTL) */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: '15px', fontWeight: 700, color: '#1E293B', mb: 2 }}>
                        תצוגה מקדימה
                    </Typography>
                    <Box sx={{
                        width: '100%',
                        minHeight: '200px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#F8FAFC',
                        borderRadius: '12px',
                        p: 2
                    }}>
                        {generating && !previewUrl ? (
                            <CircularProgress size={32} sx={{ color: '#14B8A6' }} />
                        ) : previewUrl ? (
                            <Box sx={{ position: 'relative' }}>
                                <img
                                    src={previewUrl}
                                    alt="Preview"
                                    style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: '8px', boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }}
                                />
                                {generating && (
                                    <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.6)', borderRadius: '8px' }}>
                                        <CircularProgress size={24} sx={{ color: '#14B8A6' }} />
                                    </Box>
                                )}
                            </Box>
                        ) : null}
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2, pt: 0, gap: 1 }}>
                <Button
                    onClick={handleDownload}
                    disabled={!previewUrl || generating}
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    sx={{
                        textTransform: 'none',
                        fontSize: '14px',
                        fontWeight: 600,
                        borderRadius: '10px',
                        px: 3,
                        py: 1,
                        backgroundColor: '#14B8A6',
                        '&:hover': { backgroundColor: '#0D9488' }
                    }}
                >
                    הורד תמונה
                </Button>
                <Button
                    onClick={onClose}
                    sx={{ textTransform: 'none', color: '#64748B', fontSize: '14px', borderRadius: '10px', px: 3, py: 1, border: '1px solid #E2E8F0' }}
                >
                    ביטול
                </Button>
            </DialogActions>
            </RTL>
        </Dialog>
    );
};

// --- Weight pill, overlaid at the bottom edge of a photo so it doesn't cover the body ---
const PhotoWeightBadge = ({ photo, large = false }) => {
    const label = formatPhotoWeight(photo);
    if (!label) return null;
    return (
        <Box
            data-testid="photo-weight-badge"
            sx={{
                position: 'absolute',
                bottom: large ? 10 : 6,
                left: '50%',
                transform: 'translateX(-50%)',
                px: large ? 1.25 : 0.75,
                py: large ? 0.4 : 0.2,
                borderRadius: '999px',
                backgroundColor: 'rgba(15,23,42,0.6)',
                backdropFilter: 'blur(4px)',
                color: 'white',
                fontSize: large ? '13px' : '11px',
                fontWeight: 600,
                lineHeight: 1.4,
                whiteSpace: 'nowrap',
                pointerEvents: 'none'
            }}
        >
            {label}
        </Box>
    );
};

// --- Thumbnail Card ---
const PhotoThumbnail = React.memo(({ photo, compareMode, isSelected, onSelect, onView, onDelete }) => {
    const [hover, setHover] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageError, setImageError] = useState(false);
    const containerRef = useRef(null);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.disconnect();
                }
            },
            { rootMargin: '200px' }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    return (
        <Box
            ref={containerRef}
            sx={{
                position: 'relative',
                width: 120,
                height: 156,
                borderRadius: '10px',
                overflow: 'hidden',
                cursor: 'pointer',
                flexShrink: 0,
                border: isSelected ? '3px solid #14B8A6' : '2px solid transparent',
                transition: 'all 0.2s',
                backgroundColor: '#F1F5F9',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }
            }}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            onClick={() => compareMode ? onSelect(photo) : onView(photo)}
        >
            {!imageLoaded && !imageError && (
                <Box sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <CircularProgress size={24} sx={{ color: '#CBD5E1' }} />
                </Box>
            )}
            {imageError && (
                <Box sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.5
                }}>
                    <CameraIcon sx={{ fontSize: 28, color: '#CBD5E1' }} />
                    <Typography sx={{ fontSize: '10px', color: '#94A3B8' }}>שגיאה בטעינה</Typography>
                </Box>
            )}
            {isVisible && !imageError && (
                <img
                    ref={(img) => {
                        if (img && img.complete) {
                            if (img.naturalWidth > 0) setImageLoaded(true);
                            else setImageError(true);
                        }
                    }}
                    src={imageUrl(photo.photoUrl, 120)}
                    alt=""
                    decoding="async"
                    onLoad={() => setImageLoaded(true)}
                    onError={() => setImageError(true)}
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: imageLoaded ? 'block' : 'none'
                    }}
                />
            )}
            {imageLoaded && <PhotoWeightBadge photo={photo} />}
            {isSelected && (
                <Box sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    backgroundColor: '#14B8A6',
                    color: 'white',
                    borderRadius: '50%',
                    width: 28,
                    height: 28,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    fontWeight: 700
                }}>
                    {isSelected}
                </Box>
            )}
            {hover && !compareMode && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.3)',
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'flex-end',
                        p: 1
                    }}
                >
                    <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); onDelete(photo); }}
                        sx={{
                            backgroundColor: 'rgba(239,68,68,0.9)',
                            color: 'white',
                            '&:hover': { backgroundColor: '#EF4444' }
                        }}
                    >
                        <DeleteIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Box>
            )}
        </Box>
    );
});

// --- Add Photo Skeleton Card ---
const AddPhotoSkeleton = ({ onClick }) => (
    <Box
        onClick={onClick}
        sx={{
            width: 120,
            height: 156,
            borderRadius: '10px',
            border: '2px dashed #CBD5E1',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            flexShrink: 0,
            transition: 'all 0.2s',
            '&:hover': {
                borderColor: '#14B8A6',
                backgroundColor: '#F0FDFA'
            }
        }}
    >
        <UploadIcon sx={{ fontSize: 28, color: '#94A3B8', mb: 0.5 }} />
        <Typography sx={{ fontSize: '11px', color: '#94A3B8', fontWeight: 500 }}>
            הוסף תמונה
        </Typography>
    </Box>
);

// --- Date Group ---
const DateGroup = React.memo(({ date, photos, comment, compareMode, comparePhotos, onSelectCompare, onViewPhoto, onDeletePhoto, onDeleteDate, onAddPhoto, editingComment, editCommentText, onEditComment, onEditCommentChange, onSaveComment, onCancelEditComment }) => {
    const isEditing = editingComment === date;
    // Every photo of a date carries the same weigh-in.
    const weightLabel = formatPhotoWeight(photos[0]);
    return (
        <Box sx={{ mb: 4 }}>
            {/* Date Header */}
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 2,
                pb: 1,
                borderBottom: '1px solid #E2E8F0'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#1E293B' }}>
                        {formatDate(date)}
                    </Typography>
                    <Typography sx={{ fontSize: '14px', color: '#94A3B8' }}>
                        יום {getDayName(date)}
                    </Typography>
                    <Typography sx={{ fontSize: '13px', color: '#64748B', backgroundColor: '#F1F5F9', borderRadius: '12px', px: 1.5, py: 0.3 }}>
                        {photos.length} {photos.length === 1 ? 'תמונה' : 'תמונות'}
                    </Typography>
                </Box>
                {!compareMode && (
                    <IconButton
                        size="small"
                        onClick={() => onDeleteDate(date, photos.length)}
                        sx={{ color: '#94A3B8', '&:hover': { color: '#EF4444', backgroundColor: '#FEF2F2' } }}
                    >
                        <DeleteIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                )}
            </Box>

            {/* Comment */}
            {isEditing ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <RTL>
                        <TextField
                            value={editCommentText}
                            onChange={(e) => onEditCommentChange(e.target.value)}
                            size="small"
                            fullWidth
                            placeholder="הוסף הערה..."
                            inputProps={{ maxLength: 1000 }}
                            autoFocus
                            onKeyDown={(e) => { if (e.key === 'Enter') onSaveComment(); if (e.key === 'Escape') onCancelEditComment(); }}
                        />
                    </RTL>
                    <IconButton size="small" onClick={onSaveComment} sx={{ color: '#14B8A6' }}>
                        <CheckIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                    <IconButton size="small" onClick={onCancelEditComment} sx={{ color: '#94A3B8' }}>
                        <CloseIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                </Box>
            ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5 }}>
                    {comment ? (
                        <Typography sx={{ fontSize: '13px', color: '#64748B', fontStyle: 'italic' }}>
                            {comment}
                        </Typography>
                    ) : (
                        <Typography sx={{ fontSize: '12px', color: '#CBD5E1' }}>
                            ללא הערה
                        </Typography>
                    )}
                    {!compareMode && (
                        <IconButton size="small" onClick={() => onEditComment(date, comment)} sx={{ color: '#94A3B8', '&:hover': { color: '#14B8A6' } }}>
                            <EditIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                    )}
                    {weightLabel && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1.5, color: '#0F766E', backgroundColor: '#F0FDFA', borderRadius: '12px', px: 1.25, py: 0.3 }}>
                            <WeightIcon sx={{ fontSize: 16 }} />
                            <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>
                                {weightLabel}
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}

            {/* Photo Row */}
            <Box sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1.5
            }}>
                {photos.map(photo => {
                    const selectedIndex = comparePhotos.findIndex(p => p?.id === photo.id);
                    return (
                        <PhotoThumbnail
                            key={photo.id}
                            photo={photo}
                            compareMode={compareMode}
                            isSelected={selectedIndex >= 0 ? selectedIndex + 1 : false}
                            onSelect={onSelectCompare}
                            onView={onViewPhoto}
                            onDelete={onDeletePhoto}
                        />
                    );
                })}
                {!compareMode && <AddPhotoSkeleton onClick={() => onAddPhoto(date)} />}
            </Box>
        </Box>
    );
});

// --- Comparison Panel ---
const ComparisonPanel = ({ photos, onClear }) => {
    const [shareDialogOpen, setShareDialogOpen] = useState(false);

    if (!photos[0] && !photos[1]) return null;

    const bothSelected = photos[0] && photos[1];

    return (
        <>
            <Box sx={{
                mb: 4,
                p: 3,
                backgroundColor: '#F8FAFC',
                borderRadius: '16px',
                border: '2px solid #14B8A6'
            }}>
                <RTL>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#1E293B' }}>
                        השוואת תמונות
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Button
                            size="small"
                            onClick={() => setShareDialogOpen(true)}
                            disabled={!bothSelected}
                            startIcon={<ShareIcon sx={{ fontSize: '18px' }} />}
                            sx={{
                                color: bothSelected ? '#14B8A6' : '#94A3B8',
                                textTransform: 'none',
                                fontSize: '13px',
                                fontWeight: 600,
                                '&:hover': { backgroundColor: '#F0FDFA' },
                                '&.Mui-disabled': { color: '#CBD5E1' }
                            }}
                        >
                            שיתוף השוואה
                        </Button>
                        <Button
                            size="small"
                            onClick={onClear}
                            sx={{ color: '#64748B', textTransform: 'none', fontSize: '13px' }}
                        >
                            ניקוי בחירה
                        </Button>
                    </Box>
                </Box>
                </RTL>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', gap: 0 }}>
                    {[0, 1].map(idx => (
                        <Box key={idx} sx={{ textAlign: 'center', flex: '1 1 0', minWidth: 0, maxWidth: '300px' }}>
                            {photos[idx] ? (
                                <>
                                    <Box sx={{
                                        position: 'relative',
                                        width: '100%',
                                        aspectRatio: '3 / 4',
                                        backgroundColor: '#E2E8F0',
                                        border: '2px solid #94A3B8',
                                        borderRadius: '6px',
                                        overflow: 'hidden',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        <img
                                            src={imageUrl(photos[idx].photoUrl, 300)}
                                            alt={`Compare ${idx + 1}`}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                                display: 'block'
                                            }}
                                        />
                                        <PhotoWeightBadge photo={photos[idx]} large />
                                    </Box>
                                    <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1E293B', mt: 1 }}>
                                        {formatDate(photos[idx].photoDate)}
                                    </Typography>
                                    <Typography sx={{ fontSize: '12px', color: '#94A3B8' }}>
                                        יום {getDayName(photos[idx].photoDate)}
                                    </Typography>
                                </>
                            ) : (
                                <Box sx={{
                                    width: '100%',
                                    aspectRatio: '3 / 4',
                                    backgroundColor: '#E2E8F0',
                                    borderRadius: '6px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <Typography sx={{ color: '#94A3B8', fontSize: '14px' }}>
                                        {idx === 0 ? 'בחר תמונה ראשונה' : 'בחר תמונה שנייה'}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    ))}
                </Box>
            </Box>
            {bothSelected && (
                <SharePreviewDialog
                    open={shareDialogOpen}
                    onClose={() => setShareDialogOpen(false)}
                    photo1={photos[0]}
                    photo2={photos[1]}
                />
            )}
        </>
    );
};

// --- Fullscreen Lightbox ---
const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const ZOOM_STEP = 0.5;

const FullScreenViewer = ({ photo, photos, onClose, onNavigate }) => {
    const currentIndex = photo ? photos.findIndex(p => p.id === photo.id) : -1;
    const hasPrev = currentIndex > 0;
    const hasNext = currentIndex < photos.length - 1;

    const [zoom, setZoom] = useState(1);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [dragging, setDragging] = useState(false);
    const dragStart = React.useRef({ x: 0, y: 0 });
    const posStart = React.useRef({ x: 0, y: 0 });

    const resetZoom = useCallback(() => {
        setZoom(1);
        setPosition({ x: 0, y: 0 });
    }, []);

    const handleZoomIn = useCallback(() => {
        setZoom(z => Math.min(z + ZOOM_STEP, MAX_ZOOM));
    }, []);

    const handleZoomOut = useCallback(() => {
        setZoom(z => {
            const next = Math.max(z - ZOOM_STEP, MIN_ZOOM);
            if (next === MIN_ZOOM) setPosition({ x: 0, y: 0 });
            return next;
        });
    }, []);

    const handleWheel = useCallback((e) => {
        e.preventDefault();
        if (e.deltaY < 0) handleZoomIn();
        else handleZoomOut();
    }, [handleZoomIn, handleZoomOut]);

    // Reset zoom when navigating to a different photo
    useEffect(() => {
        resetZoom();
    }, [photo?.id, resetZoom]);

    const handleMouseDown = useCallback((e) => {
        if (zoom <= 1) return;
        e.preventDefault();
        setDragging(true);
        dragStart.current = { x: e.clientX, y: e.clientY };
        posStart.current = { ...position };
    }, [zoom, position]);

    const handleMouseMove = useCallback((e) => {
        if (!dragging) return;
        setPosition({
            x: posStart.current.x + (e.clientX - dragStart.current.x),
            y: posStart.current.y + (e.clientY - dragStart.current.y)
        });
    }, [dragging]);

    const handleMouseUp = useCallback(() => {
        setDragging(false);
    }, []);

    const handleKeyDown = useCallback((e) => {
        if (!photo) return;
        if (e.key === 'ArrowLeft' && hasNext) onNavigate(photos[currentIndex + 1]);
        if (e.key === 'ArrowRight' && hasPrev) onNavigate(photos[currentIndex - 1]);
        if (e.key === 'Escape') onClose();
        if (e.key === '+' || e.key === '=') handleZoomIn();
        if (e.key === '-') handleZoomOut();
        if (e.key === '0') resetZoom();
    }, [photo, currentIndex, photos, hasPrev, hasNext, onNavigate, onClose, handleZoomIn, handleZoomOut, resetZoom]);

    useEffect(() => {
        if (!photo) return;
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [photo, handleKeyDown]);

    if (!photo) return null;

    const isZoomed = zoom > 1;

    return (
        <Dialog open onClose={onClose} maxWidth="lg" fullWidth PaperProps={{
            sx: { backgroundColor: '#0F172A', borderRadius: '16px', overflow: 'hidden' }
        }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2 }}>
                <Typography sx={{ color: 'white', fontSize: '14px' }}>
                    {formatDate(photo.photoDate)}{formatPhotoWeight(photo) ? ` · ${formatPhotoWeight(photo)}` : ''} — {currentIndex + 1}/{photos.length}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {/* Zoom controls */}
                    <IconButton onClick={handleZoomOut} disabled={zoom <= MIN_ZOOM} sx={{ color: 'white', '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' } }} size="small">
                        <ZoomOutIcon fontSize="small" />
                    </IconButton>
                    <Typography sx={{ color: 'white', fontSize: '12px', minWidth: '40px', textAlign: 'center', userSelect: 'none' }}>
                        {Math.round(zoom * 100)}%
                    </Typography>
                    <IconButton onClick={handleZoomIn} disabled={zoom >= MAX_ZOOM} sx={{ color: 'white', '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' } }} size="small">
                        <ZoomInIcon fontSize="small" />
                    </IconButton>
                    {isZoomed && (
                        <IconButton onClick={resetZoom} sx={{ color: 'white', ml: 0.5 }} size="small">
                            <ResetIcon fontSize="small" />
                        </IconButton>
                    )}
                    <IconButton onClick={onClose} sx={{ color: 'white', ml: 1 }}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </Box>

            {/* Image */}
            <Box
                sx={{
                    position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    minHeight: '60vh', px: 2, pb: 2, overflow: 'hidden',
                    cursor: isZoomed ? (dragging ? 'grabbing' : 'grab') : 'default'
                }}
                onWheel={handleWheel}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
            >
                {hasPrev && !isZoomed && (
                    <IconButton
                        onClick={() => onNavigate(photos[currentIndex - 1])}
                        sx={{ position: 'absolute', right: 16, color: 'white', backgroundColor: 'rgba(255,255,255,0.1)', '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' }, zIndex: 1 }}
                    >
                        <ChevronRight />
                    </IconButton>
                )}
                <img
                    src={photo.photoUrl}
                    alt={`Progress ${photo.photoDate}`}
                    draggable={false}
                    style={{
                        maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain', borderRadius: '8px',
                        transform: `scale(${zoom}) translate(${position.x / zoom}px, ${position.y / zoom}px)`,
                        transition: dragging ? 'none' : 'transform 0.2s ease'
                    }}
                />
                {hasNext && !isZoomed && (
                    <IconButton
                        onClick={() => onNavigate(photos[currentIndex + 1])}
                        sx={{ position: 'absolute', left: 16, color: 'white', backgroundColor: 'rgba(255,255,255,0.1)', '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' }, zIndex: 1 }}
                    >
                        <ChevronLeft />
                    </IconButton>
                )}
            </Box>
        </Dialog>
    );
};

// --- Upload Dialog ---
const UploadDialog = ({ open, onClose, traineeId, onUploadComplete, initialDate }) => {
    const [selectedDate, setSelectedDate] = useState(dayjs());
    const [files, setFiles] = useState([]);
    const [comment, setComment] = useState('');
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        if (open) {
            setSelectedDate(initialDate ? dayjs(initialDate) : dayjs());
        }
    }, [open, initialDate]);
    const [uploadPhase, setUploadPhase] = useState('idle');
    const [uploadProgress, setUploadProgress] = useState(0);
    const [currentFileIndex, setCurrentFileIndex] = useState(0);
    const [totalFiles, setTotalFiles] = useState(0);

    const { notificationHandler } = useMessageContext();

    const handleFileChange = (e) => {
        if (e.target.files) {
            setFiles(Array.from(e.target.files));
        }
    };

    const handleUpload = async () => {
        if (files.length === 0) return;

        setUploading(true);
        setTotalFiles(files.length);
        const dateStr = selectedDate.format('YYYY-MM-DD');

        try {
            for (let i = 0; i < files.length; i++) {
                setCurrentFileIndex(i + 1);
                setUploadPhase('uploading');
                setUploadProgress(0);

                const cdnUrl = await uploadImage(
                    files[i],
                    (percent) => { setUploadPhase('uploading'); setUploadProgress(percent); },
                    (percent) => { setUploadPhase('compressing'); setUploadProgress(percent); }
                );

                await addProgressPhoto(traineeId, cdnUrl, dateStr);
            }

            if (comment.trim()) {
                await saveDateComment(traineeId, dateStr, comment.trim());
            }

            setUploadPhase('complete');
            notificationHandler.success(`${files.length} תמונות הועלו בהצלחה`);
            onUploadComplete();
            setTimeout(() => {
                handleClose();
            }, 500);
        } catch (error) {
            console.error('Upload error:', error);
            setUploadPhase('error');
            notificationHandler.error('שגיאה בהעלאת התמונות');
        } finally {
            setUploading(false);
        }
    };

    const handleClose = () => {
        if (uploading) return;
        setFiles([]);
        setComment('');
        setUploadPhase('idle');
        setUploadProgress(0);
        setCurrentFileIndex(0);
        setSelectedDate(dayjs());
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{
            sx: { borderRadius: '16px', direction: 'rtl' }
        }}>
            <DialogTitle sx={{ fontWeight: 700, fontSize: '18px', color: '#1E293B', borderBottom: '1px solid #E2E8F0' }}>
                העלאת תמונות התקדמות
            </DialogTitle>
            <DialogContent sx={{ pt: '20px !important' }}>
                {/* Date Picker */}
                <Box sx={{ mb: 3 }}>
                    <RTL>
                        <DatePicker
                            label="תאריך צילום"
                            value={selectedDate}
                            onChange={(val) => val && setSelectedDate(val)}
                            format="DD/MM/YYYY"
                            maxDate={dayjs()}
                            slotProps={{ textField: { fullWidth: true } }}
                        />
                    </RTL>
                </Box>

                {/* Notes */}
                <Box sx={{ mb: 3 }}>
                    <RTL>
                        <TextField
                            label="הערות (אופציונלי)"
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            fullWidth
                            multiline
                            rows={2}
                            disabled={uploading}
                            inputProps={{ maxLength: 1000 }}
                        />
                    </RTL>
                </Box>

                {/* File Input */}
                <Box
                    component="label"
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 4,
                        border: '2px dashed #CBD5E1',
                        borderRadius: '12px',
                        cursor: uploading ? 'default' : 'pointer',
                        backgroundColor: '#F8FAFC',
                        transition: 'all 0.2s',
                        '&:hover': uploading ? {} : {
                            borderColor: '#14B8A6',
                            backgroundColor: '#F0FDFA'
                        },
                        mb: 2
                    }}
                >
                    <input
                        type="file"
                        accept="image/*"
                        multiple
                        hidden
                        disabled={uploading}
                        onChange={handleFileChange}
                    />
                    <CameraIcon sx={{ fontSize: 40, color: '#94A3B8', mb: 1 }} />
                    <Typography sx={{ fontSize: '14px', color: '#64748B', fontWeight: 500 }}>
                        {files.length > 0 ? `${files.length} קבצים נבחרו` : 'לחץ לבחירת תמונות'}
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: '#94A3B8', mt: 0.5 }}>
                        ניתן לבחור מספר תמונות
                    </Typography>
                </Box>

                {/* File preview */}
                {files.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                        {files.map((file, idx) => (
                            <Box key={idx} sx={{
                                width: 60, height: 60, borderRadius: '8px', overflow: 'hidden',
                                border: '1px solid #E2E8F0'
                            }}>
                                <img
                                    src={URL.createObjectURL(file)}
                                    alt={`Preview ${idx + 1}`}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                            </Box>
                        ))}
                    </Box>
                )}

                {/* Upload Progress */}
                {uploadPhase !== 'idle' && (
                    <Box sx={{ mb: 1 }}>
                        {totalFiles > 1 && (
                            <Typography sx={{ fontSize: '12px', color: '#64748B', mb: 1, textAlign: 'center' }}>
                                מעלה {currentFileIndex}/{totalFiles}
                            </Typography>
                        )}
                        <UploadProgressIndicator
                            phase={uploadPhase}
                            uploadProgress={uploadProgress}
                            compressionProgress={uploadPhase === 'compressing' ? uploadProgress : 0}
                            compact
                            hideCompression
                        />
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, borderTop: '1px solid #E2E8F0' }}>
                <Button onClick={handleClose} disabled={uploading} sx={{ color: '#64748B', textTransform: 'none' }}>
                    ביטול
                </Button>
                <Button
                    onClick={handleUpload}
                    disabled={files.length === 0 || uploading}
                    variant="contained"
                    sx={{
                        backgroundColor: '#14B8A6',
                        textTransform: 'none',
                        borderRadius: '8px',
                        fontWeight: 500,
                        boxShadow: 'none',
                        '&:hover': { backgroundColor: '#0D9488', boxShadow: 'none' },
                        '&:disabled': { backgroundColor: '#E2E8F0' }
                    }}
                >
                    {uploading ? 'מעלה...' : 'העלאה'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// === MAIN PAGE ===
const ProgressPhotosPage = () => {
    const navigate = useNavigate();
    const { userId } = useParams();

    const [photos, setPhotos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);

    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [uploadPrefilledDate, setUploadPrefilledDate] = useState(null);
    const [compareMode, setCompareMode] = useState(false);
    const [comparePhotos, setComparePhotos] = useState([null, null]);
    const [fullScreenPhoto, setFullScreenPhoto] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [editingComment, setEditingComment] = useState(null);
    const [editCommentText, setEditCommentText] = useState('');

    const { notificationHandler } = useMessageContext();

    const fetchPhotos = async (pageId = 0, append = false) => {
        setLoading(true);
        try {
            const response = await getProgressPhotos(userId, pageId, PAGE_SIZE);
            const pageData = response.data.body;
            const content = pageData.content || [];

            if (append) {
                setPhotos(prev => [...prev, ...content]);
            } else {
                setPhotos(content);
            }
            setHasMore(!pageData.last);
            setPage(pageId);
        } catch (err) {
            console.error('Error fetching photos:', err);
            notificationHandler.error('שגיאה בטעינת התמונות');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (userId) {
            fetchPhotos(0);
        }
    }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Group photos by date
    const photosByDate = useMemo(() => {
        const grouped = {};
        photos.forEach(photo => {
            const dateKey = toISODateKey(photo.photoDate);
            if (!grouped[dateKey]) {
                grouped[dateKey] = { photos: [], comment: '' };
            }
            grouped[dateKey].photos.push(photo);
            if (photo.dateComment) grouped[dateKey].comment = photo.dateComment;
        });
        return Object.entries(grouped)
            .sort(([a], [b]) => b.localeCompare(a))
            .map(([date, data]) => ({ date, photos: data.photos, comment: data.comment }));
    }, [photos]);

    const handleLoadMore = () => {
        if (hasMore && !loading) {
            fetchPhotos(page + 1, true);
        }
    };

    // Compare mode
    const handleSelectCompare = (photo) => {
        setComparePhotos(prev => {
            if (prev[0]?.id === photo.id) return [null, prev[1]];
            if (prev[1]?.id === photo.id) return [prev[0], null];
            if (!prev[0]) return [photo, prev[1]];
            if (!prev[1]) return [prev[0], photo];
            return [photo, prev[1]];
        });
    };

    const handleClearCompare = () => {
        setComparePhotos([null, null]);
    };

    const toggleCompareMode = () => {
        setCompareMode(prev => !prev);
        setComparePhotos([null, null]);
    };

    // Delete
    const handleDeletePhoto = (photo) => {
        setDeleteConfirm({ type: 'single', photo });
    };

    const handleDeleteDate = (date, count) => {
        setDeleteConfirm({ type: 'date', date, count });
    };

    const confirmDelete = async () => {
        if (!deleteConfirm) return;
        setLoading(true);
        try {
            if (deleteConfirm.type === 'single') {
                await deleteProgressPhoto(userId, deleteConfirm.photo.id);
                notificationHandler.success('התמונה נמחקה');
            } else {
                await deleteProgressPhotosByDate(userId, deleteConfirm.date);
                notificationHandler.success('כל התמונות מהתאריך נמחקו');
            }
            setDeleteConfirm(null);
            fetchPhotos(0);
        } catch (err) {
            console.error('Delete error:', err);
            notificationHandler.error('שגיאה במחיקה');
        } finally {
            setLoading(false);
        }
    };

    // Upload complete
    const handleUploadComplete = () => {
        fetchPhotos(0);
    };

    // Add photo to specific date
    const handleAddPhotoToDate = (date) => {
        setUploadPrefilledDate(date);
        setUploadDialogOpen(true);
    };

    const handleUploadDialogClose = () => {
        setUploadDialogOpen(false);
        setUploadPrefilledDate(null);
    };

    // Edit comment
    const handleEditComment = (date, currentComment) => {
        setEditingComment(date);
        setEditCommentText(currentComment || '');
    };

    const handleSaveComment = async () => {
        if (!editingComment) return;
        try {
            await saveDateComment(userId, editingComment, editCommentText.trim() || null);
            notificationHandler.success('ההערה עודכנה');
            setEditingComment(null);
            fetchPhotos(0);
        } catch (err) {
            console.error('Error saving comment:', err);
            notificationHandler.error('שגיאה בשמירת ההערה');
        }
    };

    // Flat photo list for lightbox navigation
    const allPhotosFlat = useMemo(() => {
        return photosByDate.flatMap(g => g.photos);
    }, [photosByDate]);

    const hasData = photos.length > 0;

    return (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Loader loading={loading} />

            {/* Header */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, px: 3, pt: 2, mb: 2 }}>
                <IconButton
                    onClick={() => navigate(`/app/clients/${userId}`)}
                    sx={{
                        color: "#64748B",
                        "&:hover": { backgroundColor: "#F1F5F9", color: "#1E293B" }
                    }}
                >
                    <ArrowForward />
                </IconButton>
                <CollectionsIcon sx={{ color: '#14B8A6', fontSize: 28 }} />
                <Typography sx={{ fontSize: "24px", fontWeight: 700, color: "#1E293B" }}>
                    תמונות התקדמות
                </Typography>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ px: 3, mb: 3, display: "flex", gap: 2, alignItems: "center" }}>
                <RTL>
                    <Button
                        variant="contained"
                        startIcon={<UploadIcon />}
                        onClick={() => setUploadDialogOpen(true)}
                        sx={{
                            backgroundColor: "#14B8A6",
                            color: "white",
                            textTransform: "none",
                            borderRadius: "8px",
                            padding: "8px 20px",
                            fontWeight: 500,
                            fontSize: "14px",
                            boxShadow: "none",
                            '&:hover': { backgroundColor: "#0D9488", boxShadow: "none" }
                        }}
                    >
                        העלאת תמונה
                    </Button>
                </RTL>
                <RTL>
                    <Button
                        variant={compareMode ? "contained" : "outlined"}
                        startIcon={<CompareIcon />}
                        onClick={toggleCompareMode}
                        disabled={!hasData}
                        sx={{
                            backgroundColor: compareMode ? '#6366F1' : 'transparent',
                            borderColor: compareMode ? '#6366F1' : "#E2E8F0",
                            color: compareMode ? 'white' : "#64748B",
                            textTransform: "none",
                            borderRadius: "8px",
                            padding: "8px 20px",
                            fontWeight: 500,
                            fontSize: "14px",
                            boxShadow: "none",
                            '&:hover': {
                                backgroundColor: compareMode ? '#4F46E5' : "#F8FAFC",
                                borderColor: compareMode ? '#4F46E5' : "#CBD5E1",
                                boxShadow: "none"
                            }
                        }}
                    >
                        {compareMode ? 'ביטול השוואה' : 'מצב השוואה'}
                    </Button>
                </RTL>
            </Box>

            {/* Content */}
            <Box sx={{ margin: "0 40px 40px" }}>
                {/* Comparison Panel */}
                {compareMode && (
                    <ComparisonPanel photos={comparePhotos} onClear={handleClearCompare} />
                )}

                {/* Gallery */}
                {!hasData && !loading ? (
                    <Box sx={{
                        textAlign: 'center',
                        py: 8,
                        backgroundColor: '#F8FAFC',
                        borderRadius: '16px'
                    }}>
                        <CameraIcon sx={{ fontSize: 56, color: '#CBD5E1', mb: 2 }} />
                        <Typography sx={{ color: '#94A3B8', fontSize: '16px', mb: 1 }}>
                            אין תמונות התקדמות עדיין
                        </Typography>
                        <Typography sx={{ color: '#CBD5E1', fontSize: '14px' }}>
                            לחץ על "העלאת תמונה" כדי להוסיף תמונות
                        </Typography>
                    </Box>
                ) : (
                    <>
                        {photosByDate.map(group => (
                            <DateGroup
                                key={group.date}
                                date={group.date}
                                photos={group.photos}
                                comment={group.comment}
                                compareMode={compareMode}
                                comparePhotos={comparePhotos}
                                onSelectCompare={handleSelectCompare}
                                onViewPhoto={setFullScreenPhoto}
                                onDeletePhoto={handleDeletePhoto}
                                onDeleteDate={handleDeleteDate}
                                onAddPhoto={handleAddPhotoToDate}
                                editingComment={editingComment}
                                editCommentText={editCommentText}
                                onEditComment={handleEditComment}
                                onEditCommentChange={setEditCommentText}
                                onSaveComment={handleSaveComment}
                                onCancelEditComment={() => setEditingComment(null)}
                            />
                        ))}

                        {/* Load More */}
                        {hasMore && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                <Button
                                    onClick={handleLoadMore}
                                    disabled={loading}
                                    sx={{
                                        color: '#14B8A6',
                                        textTransform: 'none',
                                        fontWeight: 500,
                                        fontSize: '14px',
                                        '&:hover': { backgroundColor: '#F0FDFA' }
                                    }}
                                >
                                    טען עוד...
                                </Button>
                            </Box>
                        )}

                        {/* Total count */}
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mt: 3,
                            pt: 2,
                            borderTop: '1px solid #E2E8F0'
                        }}>
                            <Typography sx={{ color: '#64748B', fontSize: '14px' }}>
                                סה״כ תמונות:
                            </Typography>
                            <Typography sx={{ color: '#1E293B', fontSize: '14px', fontWeight: 600 }}>
                                {photos.length}
                            </Typography>
                        </Box>
                    </>
                )}
            </Box>

            {/* Upload Dialog */}
            <UploadDialog
                open={uploadDialogOpen}
                onClose={handleUploadDialogClose}
                initialDate={uploadPrefilledDate}
                traineeId={userId}
                onUploadComplete={handleUploadComplete}
            />

            {/* Fullscreen Viewer */}
            {fullScreenPhoto && (
                <FullScreenViewer
                    photo={fullScreenPhoto}
                    photos={allPhotosFlat}
                    onClose={() => setFullScreenPhoto(null)}
                    onNavigate={setFullScreenPhoto}
                />
            )}

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={!!deleteConfirm}
                onClose={() => setDeleteConfirm(null)}
                PaperProps={{ sx: { borderRadius: '12px', direction: 'rtl' } }}
            >
                <DialogTitle sx={{ fontWeight: 700, fontSize: '16px', color: '#1E293B' }}>
                    {deleteConfirm?.type === 'single' ? 'מחיקת תמונה' : 'מחיקת כל התמונות מהתאריך'}
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ color: '#64748B', fontSize: '14px' }}>
                        {deleteConfirm?.type === 'single'
                            ? 'האם אתה בטוח שברצונך למחוק תמונה זו?'
                            : `האם אתה בטוח שברצונך למחוק ${deleteConfirm?.count} תמונות מתאריך ${formatDate(deleteConfirm?.date)}?`
                        }
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 2, pb: 2 }}>
                    <Button onClick={() => setDeleteConfirm(null)} sx={{ color: '#64748B', textTransform: 'none' }}>
                        ביטול
                    </Button>
                    <Button
                        onClick={confirmDelete}
                        variant="contained"
                        sx={{
                            backgroundColor: '#EF4444',
                            textTransform: 'none',
                            borderRadius: '8px',
                            boxShadow: 'none',
                            '&:hover': { backgroundColor: '#DC2626', boxShadow: 'none' }
                        }}
                    >
                        מחיקה
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ProgressPhotosPage;
